import { createRawRichTextContentStateWithHtmlStringValue, IRawRichTextContentState } from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../models/Support/ISupportTicket';
import {
	Channel,
	ThreadStatus,
	ThreadType,
	TicketPriority,
	TicketSource,
	TicketStatus,
} from '../../models/Support/SupportEnums';
import { getApiSystemMessage } from '../../utils/Support/supportHelpers';
import { escapeHtml } from '../Admin/Calendar/textToHtmlUtils';
import { invalidateTicket, invalidateTicketLists, SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

export function useUpdateTicketStatusMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (status: TicketStatus) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.status(ticketId), 'PUT', status),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export interface SnoozeTicketPayload {
	/** ISO instant the ticket wakes. */
	until: string;
	/** Saved as an internal note; blank means none. */
	note?: string;
}

/** The API takes rich content so the note can grow an editor later; one escaped paragraph per line for now. */
export function toSnoozeNoteContent(note: string | undefined): IRawRichTextContentState | undefined {
	const trimmed = note?.trim();
	if (!trimmed) {
		return undefined;
	}
	const html = trimmed
		.split(/\r?\n/)
		.map(line => `<p>${escapeHtml(line)}</p>`)
		.join('');
	return createRawRichTextContentStateWithHtmlStringValue(html);
}

/** Parks the ticket until `until`. It keeps its assignee and comes back Active. */
export function useSnoozeTicketMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: SnoozeTicketPayload) =>
			userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.snooze(ticketId), 'PUT', {
				until: payload.until,
				note: toSnoozeNoteContent(payload.note),
			}),
		onSuccess: updated => {
			if (updated) {
				queryClient.setQueryData(SUPPORT_QUERY_KEYS.tickets.detail(ticketId), updated);
			}
			invalidateTicket(queryClient, ticketId);
			// The note lands on the timeline as a thread.
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(ticketId) });
			invalidateTicketLists(queryClient);
		},
	});
}

export function useUnsnoozeTicketMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.snooze(ticketId), 'DELETE'),
		onSuccess: updated => {
			if (updated) {
				queryClient.setQueryData(SUPPORT_QUERY_KEYS.tickets.detail(ticketId), updated);
			}
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useAssignTicketMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (assigneeId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.assign(ticketId), 'PUT', {
				assigneeId: assigneeId || null,
			}),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useUpdateTicketPriorityMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (priority: TicketPriority) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.priority(ticketId), 'PUT', priority),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export function useUpdateTicketCategoryMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (categoryId: string | null) =>
			userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.category(ticketId), 'PUT', {
				categoryId,
			}),
		onSuccess: updated => {
			// Seeded from the response, not just invalidated: close actions gate on the ticket's category, and
			// a refetch-shaped delay lets a rep who just set one still be told to set one.
			if (updated) {
				queryClient.setQueryData(SUPPORT_QUERY_KEYS.tickets.detail(ticketId), updated);
			}

			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

/**
 * Moves a ticket to another mailbox (`PUT support/tickets/{id}/mailbox`). The API clears the assignee
 * and category (both are per-mailbox) and issues a fresh ticket number from the destination's counter,
 * so the response — not the request — is the only thing that knows what the ticket now looks like.
 */
export function useSetTicketMailboxMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (mailboxId: string) =>
			userSession.webServiceHelper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.mailbox(ticketId), 'PUT', {
				mailboxId,
			}),
		onSuccess: updated => {
			// Seeded from the response so the panel shows the new desk and number without a refetch gap;
			// the categories and team queries are keyed by mailboxId, so they re-fetch on the new key.
			if (updated) {
				queryClient.setQueryData(SUPPORT_QUERY_KEYS.tickets.detail(ticketId), updated);
			}

			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

export type BulkTicketUpdate =
	| { field: 'status'; status: TicketStatus }
	| { field: 'priority'; priority: TicketPriority }
	| { field: 'assignee'; assigneeId: string | null }
	| { field: 'category'; categoryId: string | null }
	| { field: 'mailbox'; mailboxId: string };

interface BulkUpdateTicketsPayload {
	ticketIds: string[];
	update: BulkTicketUpdate;
}

// No batch endpoint exists, so cap concurrent per-ticket updates.
const BULK_UPDATE_CONCURRENCY = 5;

// Attempt every ticket so an isolated failure does not block unrelated updates.
export function useBulkUpdateTicketsMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	const updateOne = (ticketId: string, update: BulkTicketUpdate) => {
		const helper = userSession.webServiceHelper;
		switch (update.field) {
			case 'status':
				return helper.callAsync(SUPPORT_PATHS.tickets.status(ticketId), 'PUT', update.status);
			case 'priority':
				return helper.callAsync(SUPPORT_PATHS.tickets.priority(ticketId), 'PUT', update.priority);
			case 'assignee':
				return helper.callAsync(SUPPORT_PATHS.tickets.assign(ticketId), 'PUT', { assigneeId: update.assigneeId });
			case 'category':
				return helper.callAsync(SUPPORT_PATHS.tickets.category(ticketId), 'PUT', { categoryId: update.categoryId });
			case 'mailbox':
				return helper.callAsync(SUPPORT_PATHS.tickets.mailbox(ticketId), 'PUT', { mailboxId: update.mailboxId });
			default:
				// Guards unchecked call-boundary casts if the union grows.
				throw new Error('Unknown bulk ticket update field.');
		}
	};

	return useMutation({
		mutationFn: async ({ ticketIds, update }: BulkUpdateTicketsPayload) => {
			let failureCount = 0;
			let reportedReason = '';
			for (let index = 0; index < ticketIds.length; index += BULK_UPDATE_CONCURRENCY) {
				const chunk = ticketIds.slice(index, index + BULK_UPDATE_CONCURRENCY);
				const results = await Promise.allSettled(chunk.map(ticketId => updateOne(ticketId, update)));
				for (const result of results) {
					if (result.status !== 'rejected') continue;
					failureCount++;
					if (!reportedReason) reportedReason = getApiSystemMessage(result.reason) ?? '';
				}
			}
			if (failureCount > 0) {
				// Labelled as one reason among possibly several: a mixed batch has no single cause, and this is
				// the earliest failure that carried an API message, not necessarily the earliest failure.
				const reason = reportedReason ? ` Reason: ${reportedReason}` : '';
				throw new Error(`Failed to update ${failureCount} of ${ticketIds.length} tickets.${reason}`);
			}
		},
		// Partial failures can still mutate tickets, so always refresh caches.
		onSettled: (_data, _error, { ticketIds }) => {
			for (const ticketId of ticketIds) {
				invalidateTicket(queryClient, ticketId);
			}
			invalidateTicketLists(queryClient);
		},
	});
}

export interface CreateTicketContact {
	/** Known-user link; the server resolves email, name, and account from the user. */
	userId?: string;
	email?: string;
	name?: string;
}

interface CreateTicketPayloadBase {
	subject: string;
	mailboxId: string;
	priority: TicketPriority;
	categoryId?: string;
	accountId?: string;
}

export interface CreateInternalTicketPayload extends CreateTicketPayloadBase {
	mode: 'internal';
	/** Editor HTML for the first thread's body; the API sanitizes every thread body on write. */
	descriptionHtml: string;
	channel: Channel;
	contact?: CreateTicketContact;
	/** Already-uploaded attachment ids (`POST email/attachment`); attached to the description thread. */
	attachmentIds?: string[];
}

/** Created with no threads: the first email is written in the composer, and the contact is its recipient. */
export interface CreateOutboundTicketPayload extends CreateTicketPayloadBase {
	mode: 'outbound';
	contact: CreateTicketContact;
}

export type CreateTicketPayload = CreateInternalTicketPayload | CreateOutboundTicketPayload;

export interface CreateTicketResult {
	ticket: ISupportTicket;
	/** Human-readable fragments for follow-up steps that failed after the ticket itself was created. */
	warnings: string[];
}

/**
 * Creates a ticket from the new-ticket form. `POST support/tickets` only binds
 * subject/priority/mailbox/category/manual-contact, so the contact link, account link, and the
 * description (the ticket's first thread) are follow-up calls. Once the ticket exists, a failed
 * follow-up is reported as a warning rather than failing the whole submission — the agent can
 * finish the linking from the ticket view.
 */
export function useCreateTicketMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: CreateTicketPayload): Promise<CreateTicketResult> => {
			const helper = userSession.webServiceHelper;
			const { subject, mailboxId, priority, categoryId, accountId, contact } = payload;
			const isOutbound = payload.mode === 'outbound';
			const isKnownContact = !!contact?.userId;

			const ticket = await helper.callAsync<ISupportTicket>(SUPPORT_PATHS.tickets.create, 'POST', {
				subject,
				priority,
				// Only an internal ticket can record that it came from a phone call.
				channel: isOutbound ? Channel.Email : payload.channel,
				mailboxId,
				categoryId: categoryId || undefined,
				source: isOutbound ? TicketSource.Outbound : TicketSource.Manual,
				// A known contact is linked via the customer endpoint below; only manual contacts bind here.
				contactEmail: isKnownContact ? undefined : contact?.email || undefined,
				contactName: isKnownContact ? undefined : contact?.name || undefined,
			});

			const warnings: string[] = [];
			const tryStep = async (failureLabel: string, step: () => Promise<unknown>) => {
				try {
					await step();
					return true;
				} catch {
					warnings.push(failureLabel);
					return false;
				}
			};

			if (isKnownContact) {
				const linked = await tryStep('link the contact', () =>
					helper.callAsync(SUPPORT_PATHS.tickets.customer(ticket.id), 'PUT', { userId: contact.userId })
				);
				// The customer link carries the account; fall back to a direct account link if it failed.
				if (!linked && accountId) {
					await tryStep('link the account', () =>
						helper.callAsync(SUPPORT_PATHS.tickets.account(ticket.id), 'PUT', { accountId })
					);
				}
			} else if (accountId) {
				await tryStep('link the account', () =>
					helper.callAsync(SUPPORT_PATHS.tickets.account(ticket.id), 'PUT', { accountId })
				);
			}

			if (payload.mode === 'outbound') {
				// Keeps an abandoned ticket in the creator's queue, not the shared pool. Failure is only a
				// warning — the composer already prompts to self-assign on first send.
				const selfId = userSession.user?.id;
				if (selfId) {
					await tryStep('assign it to you', () =>
						helper.callAsync(SUPPORT_PATHS.tickets.assign(ticket.id), 'PUT', { assigneeId: selfId })
					);
				}

				// No thread: the first email is written and sent from the composer.
				return { ticket, warnings };
			}

			const body = payload.descriptionHtml.trim();
			const threadAttachmentIds = payload.attachmentIds?.length ? payload.attachmentIds : undefined;
			if (body || threadAttachmentIds) {
				// The description is the customer's request even before a contact is known. Without an
				// author, reply-recipient resolution skips it and falls back to the ticket's contact.
				await tryStep('add the description', () =>
					helper.callAsync(`${SUPPORT_PATHS.threads.list(ticket.id)}?initialMessage=true`, 'POST', {
						type: ThreadType.CustomerMessage,
						status: ThreadStatus.Received,
						body,
						authorEmail: contact?.email || undefined,
						authorName: contact?.name || undefined,
						attachmentIds: threadAttachmentIds,
					})
				);
			}

			return { ticket, warnings };
		},
		onSuccess: ({ ticket }) => {
			invalidateTicket(queryClient, ticket.id);
			invalidateTicketLists(queryClient);
		},
	});
}

interface ChangeCustomerPayload {
	userId?: string;
	contactEmail?: string;
	contactName?: string;
}

/** Change the customer/contact attached to a support ticket. */
export function useChangeCustomerMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ChangeCustomerPayload) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.customer(ticketId), 'PUT', payload),
		onSuccess: () => {
			invalidateTicket(queryClient, ticketId);
			invalidateTicketLists(queryClient);
		},
	});
}

/** Merge a source ticket into the target ticket. All threads move to the target ticket. */
export function useMergeTicketMutation(targetTicketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (sourceTicketId: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.tickets.merge(targetTicketId, sourceTicketId), 'POST'),
		onSuccess: (_data, sourceTicketId) => {
			invalidateTicket(queryClient, targetTicketId);
			invalidateTicket(queryClient, sourceTicketId);
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(targetTicketId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.threads(sourceTicketId) });
			invalidateTicketLists(queryClient);
		},
	});
}

// --- GitHub issue mutations ---

/** Generate AI markdown for a GitHub issue from a ticket. */
export function useGenerateGitHubIssueMarkdownMutation(ticketId: string) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (description: string) =>
			userSession.webServiceHelper.callAsync<{ title: string; body: string }>(
				SUPPORT_PATHS.tickets.githubIssueMarkdown(ticketId),
				'POST',
				{ description }
			),
	});
}
